import { NavigationItemType } from './types';

/**
 * Utility functions shared between sidebar and header menu logic.
 */

/**
 * Checks if a URL starts with an exception path or child exception path.
 */
export const checkException = (item: NavigationItemType, exception: string) =>
  item.url?.startsWith(exception) ||
  !!item.children?.find(child => child.url?.startsWith(exception) && child.isPageMenu);

/**
 * Updates URLs and names of sidebar items based on page menu structure.
 */
export const updateUrls = (nav: NavigationItemType[]): NavigationItemType[] => {
  // Helper: find first "leaf" item marked isPageMenu recursively
  const findFirstPageMenuDeep = (item: NavigationItemType): NavigationItemType | null => {
    if (!item.children?.length) return item.isPageMenu ? item : null;

    // If item itself isPageMenu, check its children deeper
    const childPageMenus = item.children.filter(c => c.isPageMenu);

    // If all children are isPageMenu — go deeper
    if (childPageMenus.length === item.children.length && childPageMenus.length > 0) {
      for (const child of childPageMenus) {
        const found = findFirstPageMenuDeep(child);
        if (found) return found;
      }
    }

    // Otherwise — return current if marked
    return item.isPageMenu ? item : null;
  };

  return nav.map(item => {
    const firstHeader = item.children?.find(c => c.isPageMenu);
    const deepFirstPageMenu = firstHeader ? findFirstPageMenuDeep(firstHeader) : null;

    const effectiveHeader = deepFirstPageMenu || firstHeader;

    const isSameUrl = !effectiveHeader || effectiveHeader.url === item.url;
    const useOwnName =
      !effectiveHeader ||
      (isSameUrl && item.children?.length! > 1) ||
      item.shouldUseCurrentName ||
      !!item.thirdLevelMenuChildren;

    return {
      ...item,
      url: isSameUrl ? item.url : effectiveHeader!.url,
      name: useOwnName ? item.name : effectiveHeader!.name,
      children: useOwnName ? updateUrls(item.children || []) : item.children,
    };
  });
};

/**
 * Finds the first top-level or immediate child item that matches the current route.
 */
export const findTopLevelActiveItem = (items: NavigationItemType[], pathname: string): NavigationItemType | null => {
  const isMatch = (element?: NavigationItemType) => {
    if (!element?.url) return false;
    return (
      pathname === element.url ||
      pathname.startsWith(`${element.url}/`) ||
      element.includeUrls?.some(ex => pathname.startsWith(ex))
    );
  };

  const findPath = (nodes?: NavigationItemType[]): NavigationItemType[] | null => {
    if (!nodes?.length) return null;
    for (const node of nodes) {
      if (isMatch(node)) return [node];
      const childPath = findPath(node.children);
      if (childPath) return [node, ...childPath];
    }
    return null;
  };

  for (const top of items) {
    if (isMatch(top) && !top.isGeneral) return top;
    if (top.children?.length) {
      const path = findPath(top.children);
      if (path) {
        const firstChild = path[0];
        return firstChild.isPageMenu ? top : firstChild;
      }
    }
  }

  return null;
};

/**
 * Builds a flattened list of URLs for each sidebar node.
 */
export const collectUrls = (items: NavigationItemType[]): NavigationItemType[] =>
  items.map(item => {
    const children = item.children ? collectUrls(item.children) : [];
    const urls = [item.url, ...(item?.includeUrls || []), ...children.flatMap(c => c.urls || [])];
    return { ...item, urls, children };
  });

/**
 * Filters page-menu items for desktop views.
 */
export const filterItems = (items: NavigationItemType[], isDesktop: boolean): NavigationItemType[] =>
  items
    .map(item => {
      const children = item.children ? filterItems(item.children, isDesktop) : [];
      if (isDesktop && item.isPageMenu) return null;
      return { ...item, children };
    })
    .filter(Boolean) as NavigationItemType[];
